import { useState } from 'react';
import { Github, ExternalLink, Download } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { projects } from '../data/projects';

function Preview({ project, language }) {
  const [errored, setErrored] = useState(false);

  if (!project) return null;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={project.id}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.18 }}
      >
        <div className="preview-img-wrap">
          {project.image && !errored ? (
            <img
              src={project.image}
              alt={project.title}
              onError={() => setErrored(true)}
            />
          ) : (
            <div className="preview-placeholder">
              <span className="preview-placeholder-title">{project.title}</span>
            </div>
          )}
        </div>

        <div className="preview-meta">
          <span className="preview-meta-title">{project.title}</span>
          <div className="preview-meta-links">
            {project.links?.github && (
              <a href={project.links?.github} target="_blank" rel="noopener noreferrer" title="GitHub">
                <Github size={14} />
              </a>
            )}
            {project.links?.demo && (
              <a href={project.links?.demo} target="_blank" rel="noopener noreferrer" title="Demo">
                <ExternalLink size={14} />
              </a>
            )}
            {project.links?.download && (
              <a href={project.links?.download} target="_blank" rel="noopener noreferrer" title="Download">
                <Download size={14} />
              </a>
            )}
          </div>
        </div>

        <p className="preview-desc">{project.description[language]}</p>

        <div className="tags" style={{ marginTop: '12px' }}>
          {project.tech.map((tech, i) => (
            <span key={i} className="tag">{tech}</span>
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function Projects() {
  const { t, language } = useLanguage();
  const [active, setActive] = useState(null);

  return (
    <section id="projects" className="projects-v3">
      <div className="projects-v3-inner">
        <motion.div
          className="projects-v3-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="projects-v3-num">03</span>
          <div>
            <div className="section-tag" style={{ marginBottom: '8px' }}>Work</div>
            <h2>{t('projects.title')}</h2>
          </div>
        </motion.div>

        <div className="projects-hover-layout">
          {/* Left: list */}
          <div
            className="projects-hover-list"
            onMouseLeave={() => setActive(null)}
          >
            {projects.map((project, i) => (
              <motion.div
                key={project.id}
                className={`hover-row${active === i ? ' hover-row-active' : ''}`}
                onMouseEnter={() => setActive(i)}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <span className="hover-row-num">0{i + 1}</span>
                <div className="hover-row-info">
                  <h3>{project.title}</h3>
                </div>
                <div className="hover-row-links">
                  {project.links?.github && (
                    <a href={project.links?.github} target="_blank" rel="noopener noreferrer">
                      <Github size={13} />
                    </a>
                  )}
                  {project.links?.demo && (
                    <a href={project.links?.demo} target="_blank" rel="noopener noreferrer">
                      <ExternalLink size={13} />
                    </a>
                  )}
                  {project.links?.download && (
                    <a href={project.links?.download} target="_blank" rel="noopener noreferrer">
                      <Download size={13} />
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Right: sticky preview */}
          <div className="projects-hover-preview">
            <Preview project={active !== null ? projects[active] : null} language={language} />
          </div>
        </div>
      </div>
    </section>
  );
}
